import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Starfield } from "../components/Starfield";
import { Header } from "../components/Header";
import { Button } from "../components/ui/button";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TIERS = [
  {
    key: "free",
    name: "Seeker",
    price: "$0",
    period: "14-day free trial",
    lookup: null,
    features: ["3 AI sessions / week", "Reflect mode", "Journal up to 5 insights", "All five portals to try"],
  },
  {
    key: "ascendent_monthly",
    name: "Ascendent",
    price: "$7.99",
    period: "per month",
    popular: true,
    lookup: "ascendent_monthly",
    plan: "ascendent",
    features: [
      "Unlimited AI chat sessions",
      "All 5 portals, always",
      "Unlimited journal archiving",
      "Priority response model",
      "Session export",
    ],
  },
  {
    key: "luminous_yearly",
    name: "Luminous",
    price: "$59.99",
    period: "per year · save 37%",
    lookup: "luminous_yearly",
    plan: "luminous",
    features: [
      "Everything in Ascendent",
      "Over 4 months free",
      "Ascension metrics & pattern tracking",
      "Cosmic ambient soundscapes",
    ],
  },
];

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [busy, setBusy] = useState(null);
  const [promo, setPromo] = useState(null);

  useEffect(() => {
    api.get("/payments/promo").then(({ data }) => setPromo(data)).catch(() => {});
  }, []);

  const choose = async (tier) => {
    if (!tier.lookup) {
      navigate(user && user.user_id ? "/app" : "/login?mode=register");
      return;
    }
    if (!user || !user.user_id) {
      navigate("/login?mode=register");
      return;
    }
    setBusy(tier.key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: tier.lookup,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy(null);
    }
  };

  const current = user?.plan;

  return (
    <div className="relative min-h-screen bg-cg-bg">
      <Starfield />
      <Header />
      <section className="relative z-10 max-w-6xl mx-auto px-5 pt-32 pb-24">
        <div className="text-center mb-16">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-cg-violetglow mb-4">Choose your path</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-extralight tracking-tight">Pricing that meets your depth</h1>
          <p className="mt-4 text-cg-texts max-w-xl mx-auto">Start free. Ascend when you're ready. Cancel anytime.</p>
          {promo?.active && (
            <div data-testid="pricing-promo-banner" className="mt-6 inline-flex items-center gap-2 rounded-full border border-cg-gold/50 bg-[rgba(242,181,68,0.08)] px-4 py-2 text-sm text-cg-textp">
              <Sparkles className="h-4 w-4 text-cg-gold" />
              Early member offer: use code
              <span className="font-mono text-cg-gold tracking-wider" data-testid="pricing-promo-code">{promo.code}</span>
              at checkout for {promo.percent_off}% off your first payment
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-6 items-start">
          {TIERS.map((t, i) => (
            <motion.div
              key={t.key}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className={`relative rounded-3xl p-8 border transition-all duration-300 hover:-translate-y-1.5 ${
                t.popular
                  ? "bg-cg-card border-cg-cyan/50 shadow-[0_0_40px_rgba(92,200,240,0.18)]"
                  : "bg-cg-card border-[rgba(140,79,214,0.22)] hover:border-cg-violetglow/50"
              }`}
            >
              {t.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1 rounded-full bg-cg-cyan px-3 py-1 text-xs font-medium text-cg-bg">
                  <Sparkles className="h-3 w-3" /> Most chosen
                </span>
              )}
              <h3 className="font-serif text-2xl text-cg-textp">{t.name}</h3>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-serif text-4xl text-cg-textp">{t.price}</span>
              </div>
              <p className="text-sm text-cg-textm mt-1">{t.period}</p>

              <ul className="mt-7 space-y-3">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-cg-texts">
                    <Check className="h-4 w-4 text-cg-cyan shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>

              <Button
                data-testid={`choose-plan-${t.key}`}
                onClick={() => choose(t)}
                disabled={busy === t.key || current === t.plan}
                className={`w-full mt-8 h-11 rounded-full ${
                  t.popular
                    ? "bg-cg-cyan hover:bg-cg-cyan/90 text-cg-bg"
                    : "bg-cg-violet hover:bg-cg-violetglow text-white"
                }`}
              >
                {current === t.plan
                  ? "Current plan"
                  : busy === t.key
                  ? "Redirecting…"
                  : t.lookup
                  ? `Choose ${t.name}`
                  : "Start free"}
              </Button>
            </motion.div>
          ))}
        </div>

        <p className="text-center text-xs text-cg-textm mt-10">
          Test mode — use card 4242 4242 4242 4242, any future expiry and CVC.
        </p>
      </section>
    </div>
  );
}
